import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Loader2, Plus, Trash2, Wallet, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import SmartCombobox from "@/components/SmartCombobox";
import ManageOptionsDialog from "@/components/ManageOptionsDialog";
import { subscribeOptionsChanged } from "@/lib/optionsBus";

const today = () => new Date().toISOString().slice(0, 10);
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const emptyForm = () => ({ voucher_date: today(), party_name: "", amount: "", mode: "Cash", remarks: "" });

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters
  const [fParty, setFParty] = useState("ALL");
  const [fPeriod, setFPeriod] = useState("all"); // all | month | year | range
  const [fMonth, setFMonth] = useState(today().slice(0, 7)); // YYYY-MM
  const [fYear, setFYear] = useState(String(new Date().getFullYear()));
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [deleteId, setDeleteId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [appliedParams, setAppliedParams] = useState({});
  const [sortBy, setSortBy] = useState("date_desc");
  const [selected, setSelected] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);

  const buildParams = useCallback(() => {
    const p = {};
    if (fParty && fParty !== "ALL") p.party = fParty;
    if (fPeriod === "month" && fMonth) p.month = fMonth;
    else if (fPeriod === "year" && fYear) p.year = fYear;
    else if (fPeriod === "range") {
      if (fFrom) p.date_from = fFrom;
      if (fTo) p.date_to = fTo;
    }
    return p;
  }, [fParty, fPeriod, fMonth, fYear, fFrom, fTo]);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/expenses", { params: appliedParams });
      setExpenses(data || []);
      setSelected(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [appliedParams]);

  const fetchParties = async () => {
    try {
      const { data } = await api.get("/options/expense_parties");
      setParties(data || []);
    } catch (e) { console.error("Failed to fetch expense parties:", e); }
  };

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);
  useEffect(() => {
    fetchParties();
    const unsub = subscribeOptionsChanged("expense_parties", fetchParties);
    return () => unsub();
  }, []);

  const applyFilters = () => setAppliedParams(buildParams());

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm());
    setOpen(true);
  };

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      openCreate();
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const openEdit = (row) => {
    setEditId(row.id);
    setForm({
      voucher_date: row.voucher_date, party_name: row.party_name,
      amount: String(row.amount), mode: row.mode, remarks: row.remarks || "",
    });
    setOpen(true);
  };

  const matchesFilters = (row) => {
    const p = appliedParams;
    if (p.party && row.party_name !== p.party) return false;
    if (p.month && !(row.voucher_date || "").startsWith(p.month)) return false;
    if (p.year && !(row.voucher_date || "").startsWith(String(p.year))) return false;
    if (p.date_from && row.voucher_date < p.date_from) return false;
    if (p.date_to && row.voucher_date > p.date_to) return false;
    return true;
  };

  const submit = async () => {
    if (!form.party_name.trim()) { toast.error("Party name is required"); return; }
    if (!form.voucher_date) { toast.error("Voucher date is required"); return; }
    const amt = parseFloat(form.amount);
    if (isNaN(amt) || amt < 0) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    const payload = {
      voucher_date: form.voucher_date,
      party_name: form.party_name.trim(),
      amount: amt,
      mode: form.mode,
      remarks: form.remarks,
    };
    try {
      if (editId) {
        const { data } = await api.put(`/expenses/${editId}`, payload);
        setExpenses((prev) => {
          const next = prev.map((x) => (x.id === editId ? data : x));
          return matchesFilters(data) ? next : next.filter((x) => x.id !== editId);
        });
        toast.success("Expense updated");
      } else {
        const { data } = await api.post("/expenses", payload);
        if (matchesFilters(data)) {
          setExpenses((prev) => [data, ...prev].sort((a, b) => (b.voucher_date || "").localeCompare(a.voucher_date || "")));
        }
        toast.success("Expense recorded");
      }
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    const id = deleteId;
    const prev = expenses;
    setExpenses((p) => p.filter((x) => x.id !== id));
    setDeleteId(null);
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Expense deleted");
    } catch (e) {
      setExpenses(prev);
      toast.error(formatApiError(e));
    }
  };

  const handleExport = () => {
    const token = localStorage.getItem("access_token");
    const qs = new URLSearchParams(appliedParams).toString();
    fetch(`${API}/exports/expenses.csv${qs ? `?${qs}` : ""}`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => { if (!r.ok) throw new Error("Export failed"); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "expenses.csv"; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Export failed"));
  };

  const resetFilters = () => {
    setFParty("ALL"); setFPeriod("all"); setFFrom(""); setFTo("");
    setFMonth(today().slice(0, 7)); setFYear(String(new Date().getFullYear()));
    setAppliedParams({});
  };

  const bulkDelete = async () => {
    const ids = [...selected];
    setBulkConfirm(false);
    if (!ids.length) return;
    const prev = expenses;
    setExpenses((p) => p.filter((x) => !selected.has(x.id)));
    setSelected(new Set());
    try {
      await api.post("/expenses/bulk-delete", { ids });
      toast.success(`Deleted ${ids.length} ${ids.length > 1 ? "entries" : "entry"}`);
    } catch (e) { setExpenses(prev); toast.error(formatApiError(e)); }
  };

  const filterActive = Object.keys(appliedParams).length > 0;

  const displayed = [...expenses].sort((a, b) => {
    if (sortBy === "date_asc") return (a.voucher_date || "").localeCompare(b.voucher_date || "");
    if (sortBy === "mode") return (a.mode || "").localeCompare(b.mode || "") || (b.voucher_date || "").localeCompare(a.voucher_date || "");
    return (b.voucher_date || "").localeCompare(a.voucher_date || "");
  });

  const sum = (arr) => arr.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const total = sum(expenses);
  const cashTotal = sum(expenses.filter((e) => e.mode === "Cash"));
  const bankTotal = sum(expenses.filter((e) => e.mode === "Bank"));

  const allSelected = displayed.length > 0 && displayed.every((e) => selected.has(e.id));
  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(displayed.map((e) => e.id)));

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-wider text-[#64748B] mb-1">Module 06</div>
          <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">Store Expenses</h1>
          <p className="text-sm text-[#475569] mt-1">Record vouchers, filter by party or period, and export to CSV.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport} className="gap-2" data-testid="export-expenses-btn">
            <Download className="w-4 h-4" /> Export CSV
          </Button>
          <Button onClick={openCreate} className="bg-[#0055FF] hover:bg-[#0040C1] text-white gap-2" data-testid="add-expense-btn">
            <Plus className="w-4 h-4" /> Add Expense
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="border border-[#E2E8F0] rounded-lg bg-white p-4">
        <div className="flex items-center gap-2 mb-3 text-[11px] font-mono uppercase tracking-wider text-[#64748B]">
          <Filter className="w-3.5 h-3.5" /> Filters
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-[#475569]">Party</Label>
            <Select value={fParty} onValueChange={setFParty}>
              <SelectTrigger data-testid="filter-party"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All parties</SelectItem>
                {parties.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-[#475569]">Period</Label>
            <Select value={fPeriod} onValueChange={setFPeriod}>
              <SelectTrigger data-testid="filter-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All time</SelectItem>
                <SelectItem value="month">By month</SelectItem>
                <SelectItem value="year">By year</SelectItem>
                <SelectItem value="range">Date range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {fPeriod === "month" && (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-[#475569]">Month</Label>
              <Input type="month" value={fMonth} onChange={(e) => setFMonth(e.target.value)} data-testid="filter-month" />
            </div>
          )}
          {fPeriod === "year" && (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-[#475569]">Year</Label>
              <Input type="number" min="2000" max="2100" value={fYear} onChange={(e) => setFYear(e.target.value)} data-testid="filter-year" />
            </div>
          )}
          {fPeriod === "range" && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">From</Label>
                <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="filter-from" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#475569]">To</Label>
                <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} data-testid="filter-to" />
              </div>
            </>
          )}
          <div className="flex items-end gap-2">
            <Button onClick={applyFilters} className="bg-[#0055FF] hover:bg-[#0040C1] text-white h-10" data-testid="apply-filters">Apply</Button>
            <Button variant="ghost" onClick={resetFilters} className="text-xs text-[#475569] h-10" data-testid="reset-filters">Reset</Button>
          </div>
        </div>
      </div>

      {/* Toolbar: sort + bulk actions + conditional totals */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-xs uppercase tracking-wide text-[#475569] whitespace-nowrap">Sort by</Label>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-9 w-44" data-testid="expense-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="date_desc">Date (newest)</SelectItem>
                <SelectItem value="date_asc">Date (oldest)</SelectItem>
                <SelectItem value="mode">Mode (Bank/Cash)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {selected.size > 0 && (
            <Button variant="outline" onClick={() => setBulkConfirm(true)} className="h-9 gap-2 text-[#EF4444] border-[#EF4444]/40" data-testid="delete-selected-btn">
              <Trash2 className="w-4 h-4" /> Delete selected ({selected.size})
            </Button>
          )}
        </div>
        {filterActive && (
          <div className="flex flex-wrap gap-2 text-xs" data-testid="expense-summary">
            <span className="px-2.5 py-1 rounded-md bg-[#F1F5F9] font-mono">Total: <b>{inr(total)}</b></span>
            <span className="px-2.5 py-1 rounded-md bg-amber-50 text-amber-700 font-mono" data-testid="expense-cash-total">Cash: <b>{inr(cashTotal)}</b></span>
            <span className="px-2.5 py-1 rounded-md bg-blue-50 text-blue-700 font-mono" data-testid="expense-bank-total">Bank: <b>{inr(bankTotal)}</b></span>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="border border-[#E2E8F0] rounded-lg bg-white overflow-x-auto">
        <Table>
          <TableHeader className="bg-[#F8F9FA]">
            <TableRow>
              <TableHead className="w-10">
                <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="w-4 h-4 accent-[#0055FF] cursor-pointer" data-testid="expense-select-all" aria-label="Select all" />
              </TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Voucher Date</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Party / Vendor</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Mode</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider">Remarks</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider text-right">Amount</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-wider text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-sm text-[#64748B]">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
              </TableCell></TableRow>
            ) : displayed.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-sm text-[#64748B]" data-testid="expenses-empty">
                No expenses found.
              </TableCell></TableRow>
            ) : displayed.map((e) => (
              <TableRow key={e.id} data-testid={`expense-row-${e.id}`} data-state={selected.has(e.id) ? "selected" : undefined}>
                <TableCell onClick={(ev) => ev.stopPropagation()}>
                  <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggleSelect(e.id)} className="w-4 h-4 accent-[#0055FF] cursor-pointer" data-testid={`expense-select-${e.id}`} />
                </TableCell>
                <TableCell className="font-mono text-xs cursor-pointer" onClick={() => openEdit(e)}>{e.voucher_date}</TableCell>
                <TableCell className="text-sm font-medium cursor-pointer" onClick={() => openEdit(e)}>{e.party_name}</TableCell>
                <TableCell className="cursor-pointer" onClick={() => openEdit(e)}>
                  <span className={`text-[11px] font-mono px-2 py-0.5 rounded ${e.mode === "Bank" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"}`}>{e.mode}</span>
                </TableCell>
                <TableCell className="text-xs text-[#475569] max-w-[280px] truncate cursor-pointer" title={e.remarks} onClick={() => openEdit(e)}>{e.remarks || "—"}</TableCell>
                <TableCell className="text-right font-mono cursor-pointer" onClick={() => openEdit(e)}>{inr(e.amount)}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => setDeleteId(e.id)} data-testid={`delete-expense-${e.id}`}>
                    <Trash2 className="w-4 h-4 text-[#EF4444]" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filterActive && displayed.length > 0 && (
              <TableRow className="bg-[#F8F9FA]">
                <TableCell colSpan={5} className="text-right font-mono text-[11px] uppercase tracking-wider text-[#64748B]">Total ({displayed.length})</TableCell>
                <TableCell className="text-right font-mono font-semibold" data-testid="expenses-total">{inr(total)}</TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="expense-sheet">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editId ? "Edit expense" : "Record expense"}</DialogTitle>
            <DialogDescription>Capture a store expense voucher.</DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <div className="flex items-center h-7"><Label className="text-xs uppercase tracking-wide text-[#475569]">Voucher Date *</Label></div>
                <Input type="date" value={form.voucher_date} onChange={(e) => setForm({ ...form, voucher_date: e.target.value })} data-testid="expense-date" />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between h-7">
                  <Label className="text-xs uppercase tracking-wide text-[#475569]">Vendor / Party Name *</Label>
                  <ManageOptionsDialog optionType="expense_parties" label="Parties" testId="manage-parties" />
                </div>
                <SmartCombobox
                  value={form.party_name}
                  onChange={(v) => setForm({ ...form, party_name: v })}
                  optionType="expense_parties"
                  placeholder="Search or add party…"
                  testId="expense-party"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center h-7"><Label className="text-xs uppercase tracking-wide text-[#475569]">Amount (₹ INR) *</Label></div>
                <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="font-mono" placeholder="0.00" data-testid="expense-amount" />
              </div>

              <div className="space-y-2">
                <div className="flex items-center h-7"><Label className="text-xs uppercase tracking-wide text-[#475569]">Mode of Payment *</Label></div>
                <RadioGroup value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })} className="flex gap-6 pt-1.5">
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="Cash" id="mode-cash" data-testid="expense-mode-cash" />
                    <Label htmlFor="mode-cash" className="cursor-pointer text-sm">Cash</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="Bank" id="mode-bank" data-testid="expense-mode-bank" />
                    <Label htmlFor="mode-bank" className="cursor-pointer text-sm">Bank</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-[#475569]">Remarks</Label>
              <Textarea rows={3} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="expense-remarks" />
            </div>
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="expense-cancel">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#0055FF] hover:bg-[#0040C1] text-white gap-2" data-testid="expense-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wallet className="w-4 h-4" /> {editId ? "Save changes" : "Save expense"}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this expense?</AlertDialogTitle>
            <AlertDialogDescription>This voucher record will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-expense-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="confirm-expense-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkConfirm} onOpenChange={setBulkConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} selected {selected.size > 1 ? "entries" : "entry"}?</AlertDialogTitle>
            <AlertDialogDescription>These voucher records will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={bulkDelete} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="confirm-bulk-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
